import { Component, Injector, Input, OnInit } from '@angular/core';
import { BaseFormComponent } from '../base-form.component';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { TextMaskConfig } from 'angular2-text-mask';

@Component({
  moduleId: module.id,
  selector: 'basic-text-input',
  templateUrl: './basic-text-input.component.html',
})
export class BasicTextInputComponent extends BaseFormComponent implements OnInit {
  @Input() propertyName: string;
  @Input() placeholder: string;
  @Input() maxlength: string;
  @Input() inputType: string;
  @Input() parserType: string;
  @Input() formatterType: string;
  @Input() extraClasses: string[];
  @Input() disableConditionally: boolean = false;
  @Input() hideLabel: boolean = false;
  @Input() isSandwich: boolean = false;
  @Input() isDriversLicense: boolean = false;
  @Input() isState: boolean = false;
  @Input() inputMask?: TextMaskConfig;
  @Input() label: string;
  @Input() alwaysShowPlaceholder: boolean = false;

  extraClassesStr: string;
  val: string;
  isError: boolean = false;
  componentId: string;
  amorphicService: AmorphicService;
  isQuestionLockableNode: boolean = false;

  defaultMask: TextMaskConfig = { mask: false };

  constructor(protected injector: Injector) {
    super(injector);

    this.amorphicService = injector.get(AmorphicService);

    this.inputType = this.inputType || 'text';
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.prop = this.propertyName ? this.handleIterate(this.propertyName) : this.prop;
    this.extraClasses = this.extraClasses || [];
    this.componentId = this.getComponentId();
    this.maxlength = this.maxlength || '524288';
    if (!this.parserType) {
      this.parserType = this.deriveParser(this.prop);
    }

    if (!this.formatterType) {
      this.formatterType = this.deriveFormatter(this.prop);
    }

    if (this.placeholder == null) {
      this.placeholder = '';
    }

    this.checkIfShouldMask();

    this.updateFromModel();
    this.isQuestionLockableNode = this.questionItem.isQuestionLockableNode();

    // allow for input labels to show on date, currency, SSN inputs when in custom components
    const isCustomComponent: boolean = this.questionItem.controlType.includes('custom');
    this.noLabel =
      (!isCustomComponent && ['Date', 'CurrencyZero'].includes(this.parserType)) ||
      this.parserType === 'SSN' ||
      this.hideLabel;
  }

  async trigger(): Promise<void> {
    this.isError = !(await this.validateAndSet(this.prop, this.val));
    if (this.isError && this.resetOnError(this.prop)) {
      this.val = null;
      this.setValue(this.prop, this.val);
    }
  }

  checkIfShouldMask() {
    let isSafari = true;
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      const isChrome = navigator.userAgent.indexOf('Chrome') > -1;
      isSafari = navigator.userAgent.indexOf('Safari') > -1;
      if (isChrome && isSafari) {
        isSafari = false;
      }
    }
    if (this.shouldObfuscateInput || isSafari) {
      this.inputMask = undefined;
    }
  }

  getComponentId() {
    return this.questionItem.id + '_' + this.prop; // this.prop -> involves customProp when the question has more than one binding
  }

  resetValue(): void {
    this.val = '';
    this.isError = false;
    this.questionItem.needsFixed = false;
  }

  updateFromModel(): void {
    // check for special cases
    if (!this.shouldDisplayChangedValue()) {
      return;
    }
    this.checkIfShouldMask();
    this.val = this.getValue(this.prop);
    if (this.formatterType) {
      this.val = this.formatterSvc.format(this.formatterType, this.val);
    }
  }

  shouldDisplayChangedValue(): boolean {
    // drivers license id number and passport/state issued id numbers are bound to the same property
    // do not display id number on driver's license input if no drivers license is checked
    const hasDriversLicense =
      this.amorphicService.controller.customer.application.primaryPolicy.insured.person.hasDriversLicense;
    if (this.isDriversLicense && !hasDriversLicense) {
      return false;
    }

    // when driver's license input has an error, updateFromModel loads last valid value, which is a unique behavior for driver's license, therefore need to obfuscate.
    if (this.isDriversLicense && this.isError) {
      this.obfuscatePii(this);
    }
    return true;
  }

  format(): void {
    this.val = this.val.trim();
    this.val = this.formatterSvc.format(this.formatterType, this.val);
  }

  handleBlur() {
    if (!this.errorMessage[this.prop]) {
      this.checkEmptyAndValidate(this.prop, this.val);
    }
  }

  protected parse(prop: string, value): string {
    const parserType = this.parserType || prop;

    const result = this.parserSvc.parse(parserType, value);

    return result;
  }
}
